import { useState } from 'react'
import { X, Plus } from 'lucide-react'

interface Props {
  value: Record<string, any> | null
  onChange: (next: Record<string, string>) => void
}

// Lean key/value editor over a jsonb object column (contacts/companies.custom_fields).
// Strings only (no type inference — a deliberate v1 simplification). Keys are fixed
// once added (value editable, remove to change a key). The editor owns the full map
// and always writes the WHOLE next object, so existing keys are never clobbered.
export default function CustomFieldsEditor({ value, onChange }: Props) {
  const fields = value || {}
  const keys = Object.keys(fields)
  const [newKey, setNewKey] = useState('')
  const [newVal, setNewVal] = useState('')
  const [err, setErr] = useState('')

  const setValue = (k: string, v: string) => onChange({ ...stringify(fields), [k]: v })
  const remove = (k: string) => { const next = stringify(fields); delete next[k]; onChange(next) }
  const add = () => {
    const k = newKey.trim()
    if (!k) { setErr('Field name required'); return }
    if (keys.some(x => x.toLowerCase() === k.toLowerCase())) { setErr('That field already exists'); return }
    onChange({ ...stringify(fields), [k]: newVal.trim() })
    setNewKey(''); setNewVal(''); setErr('')
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {keys.map(k => (
        <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span title={k} style={{ flex: '0 0 38%', fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k}</span>
          <input defaultValue={String(fields[k] ?? '')}
            onBlur={e => { if (e.target.value !== String(fields[k] ?? '')) setValue(k, e.target.value) }}
            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
            style={{ flex: 1, minWidth: 0, padding: '5px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12, color: 'var(--ink-primary)', outline: 'none' }}
            onFocus={e => { e.currentTarget.style.borderColor = 'var(--focus-ring)' }} />
          <button type="button" aria-label={`Remove ${k}`} onClick={() => remove(k)}
            style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}>
            <X style={{ width: 12, height: 12 }} />
          </button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input value={newKey} onChange={e => { setNewKey(e.target.value); setErr('') }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }} placeholder="Field name"
          style={{ flex: '0 0 38%', minWidth: 0, padding: '5px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12, color: 'var(--ink-primary)', outline: 'none' }} />
        <input value={newVal} onChange={e => setNewVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }} placeholder="Value"
          style={{ flex: 1, minWidth: 0, padding: '5px 8px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 12, color: 'var(--ink-primary)', outline: 'none' }} />
        <button type="button" aria-label="Add field" onClick={add}
          style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-text)', padding: 0 }}>
          <Plus style={{ width: 14, height: 14 }} />
        </button>
      </div>
      {err && <span style={{ fontSize: 11, color: 'var(--danger)' }}>{err}</span>}
    </div>
  )
}

// Coerce the stored object to a string→string map (values may be non-strings if
// ever written elsewhere; v1 stores strings and round-trips the rest as strings).
function stringify(obj: Record<string, any>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const k of Object.keys(obj)) out[k] = String(obj[k] ?? '')
  return out
}
